/**
 * SendDirectMessageModal — envio de mensagem dirigida (Item #8b, Slice E).
 *
 * Componente único para as DUAS entradas (handoff §4):
 *   (a) "Mensagem privada" do SupervisorAttendantCard → `lockedRecipient` travado
 *       (título "Mensagem privada", sem toggle de destinatário);
 *   (b) CTA "Nova mensagem" da MensagensPage → em branco (título "Nova mensagem").
 *
 * Gate P19 vive nos PONTOS DE ENTRADA (`direct-message-initiate`): este modal só é
 * montado por quem tem a cap. O backend re-valida o escopo (alvo/time/âncora) e
 * devolve 403 com `code` — mapeado aqui em banner inline honesto (não toast).
 *
 * Fontes (reuso, não recriar):
 *   - pessoas: usePresence() (exclui o usuário atual);
 *   - times:   useTeamsList() (gate team-config-read). HONEST-DEFER: não há endpoint
 *              FE que liste "os times que eu supervisiono" por nome — usamos a lista
 *              de times do tenant e confiamos no gate de escopo do backend. Se a cap
 *              de leitura de times faltar / a lista vier vazia, o picker degrada
 *              honesto ("Nenhum time disponível"); o broadcast segue validado server-side.
 *   - âncora:  useConversations({ assignedToUserId: 'me' }) (opcional; default "Nenhuma").
 */

// ModalBase + .modal-* / .btn-ghost-mini styles vivem no CSS do supervisor (importado lá
// como side-effect de supervisor/components/index.ts). Importado explicitamente aqui para
// que este modal fique estilizado na superfície NÃO-supervisor /mensagens, independente de
// quais outras rotas foram carregadas (robustez se as rotas de supervisor forem code-split).
import '../supervisor/supervisor-compounds.css';

import { AlertTriangle, Check, ChevronDown, Link2, Lock, User, Users } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useConversations } from '../../hooks/use-conversations';
import { ApiError } from '../../lib/api-client';
import { avatarColorFor, initialsOf } from '../../lib/format';
import { ModalBase } from '../supervisor/components/modals/ModalBase';

import { useBroadcast, useInitiateThread, useRecipientScope } from './hooks/use-direct-messages';

import type { BroadcastBody, BroadcastMode, InitiateThreadBody, RecipientScope } from './types';

export interface SendDirectMessageModalProps {
  open: boolean;
  onClose: () => void;
  /** Destinatário pré-selecionado e travado (entrada via card do atendente). */
  lockedRecipient?: { userId: string; name: string } | null;
  /** Chamado no sucesso 1:1 com o threadId criado (a página navega ao thread). */
  onSent?: (threadId: string) => void;
}

type RecipientTab = 'person' | 'team';

const NO_ANCHOR = '';

/** Copy honesto por código de erro 403 do backend; fallback genérico. */
function errorMessageFor(code: string | undefined): string {
  switch (code) {
    case 'DM_TARGET_OUT_OF_SCOPE':
      return 'Esse atendente está fora do seu escopo de supervisão.';
    case 'DM_TEAM_OUT_OF_SCOPE':
      return 'Esse time está fora do seu escopo de supervisão. Selecione um time que você supervisiona.';
    case 'DM_EMPTY_TEAM':
      return 'Esse time não tem outros membros para receber a mensagem.';
    case 'DM_ANCHOR_OUT_OF_TENANT':
      return 'A conversa selecionada não está disponível.';
    default:
      return 'Não foi possível enviar a mensagem. Tente novamente.';
  }
}

function codeOf(err: unknown): string | undefined {
  return err instanceof ApiError ? (err.body as { code?: string }).code : undefined;
}

/**
 * Estado vazio do picker de pessoas EXPLICADO pela `direction` do escopo (N2-13a,
 * noturna 2 · D5). «Ninguém no seu escopo» era verdadeiro e inútil: no lab nenhum
 * time tinha líder, o escopo vinha vazio para TODOS e ninguém sabia o que consertar.
 *
 * Mecanismo do engine (`resolveInitiationScope`): quem inicia PARA CIMA (`up`) fala
 * com os líderes (`roleInTeam=lead`) dos times em que é membro (+ a quem reporta);
 * quem inicia PARA BAIXO (`down`/`both`) fala com os membros dos times que lidera;
 * lateral é opt-in. Copy neutra de vertical — não cita tenant nem papel setorial.
 */
export function emptyPeopleCopy(direction: RecipientScope['direction']): string {
  switch (direction) {
    case 'up':
      return 'Você fala com os supervisores dos seus times. Nenhum dos seus times tem um líder definido — peça ao gestor em Times & Roteamento.';
    case 'down':
    case 'both':
      return 'Você fala com os membros dos times que lidera. Você ainda não lidera nenhum time com membros.';
    case 'none':
      return 'Sua função não inicia conversas internas; você pode responder às que receber.';
  }
}

export function SendDirectMessageModal({
  open,
  onClose,
  lockedRecipient = null,
  onSent,
}: SendDirectMessageModalProps): React.JSX.Element | null {
  const locked = lockedRecipient !== null;

  const { data: conversationsResp } = useConversations({ assignedToUserId: 'me' });
  // Picker escopado: diretório de destinatários (com nomes) do backend — só quem
  // o ator pode INICIAR. Autossuficiente; não depende de presença.
  const scope = useRecipientScope();

  const initiate = useInitiateThread();
  const broadcast = useBroadcast();

  const [tab, setTab] = useState<RecipientTab>('person');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [mode, setMode] = useState<BroadcastMode>('group');
  const [anchorId, setAnchorId] = useState<string>(NO_ANCHOR);
  const [anchorOpen, setAnchorOpen] = useState(false);
  const [body, setBody] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Pessoas: diretório escopado do backend (com nomes, self já excluído).
  const people = scope.people;
  // Times: só os que o ator lidera (broadcast é descendente).
  const scopedTeams = useMemo(
    () => scope.teams.map((t) => ({ id: t.teamId, name: t.name, memberCount: t.memberCount })),
    [scope.teams],
  );

  const anchors = useMemo(() => (conversationsResp?.data ?? []).slice(0, 12), [conversationsResp]);
  const anchorLabel = useMemo(() => {
    if (anchorId === NO_ANCHOR) return 'Nenhuma';
    const conv = (conversationsResp?.data ?? []).find((c) => c.id === anchorId);
    return conv?.person?.displayName ?? '(sem identidade)';
  }, [anchorId, conversationsResp]);

  if (!open) return null;

  const trimmedBody = body.trim();
  const hasRecipient =
    locked || (tab === 'person' ? selectedUserId !== null : selectedTeamId !== null);
  const showModePicker = !locked && tab === 'team' && selectedTeamId !== null;
  const isPending = initiate.isPending || broadcast.isPending;
  const canSubmit = hasRecipient && trimmedBody.length > 0 && !isPending;

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    setErrorMsg(null);
    const anchor = anchorId === NO_ANCHOR ? undefined : anchorId;

    // 1:1 — card travado OU aba "Um atendente".
    const toUserId = locked ? lockedRecipient.userId : selectedUserId;
    if ((locked || tab === 'person') && toUserId !== null) {
      const payload: InitiateThreadBody = {
        toUserId,
        body: trimmedBody,
        ...(anchor !== undefined ? { anchorConversationId: anchor } : {}),
      };
      initiate.mutate(payload, {
        onSuccess: (res) => {
          onSent?.(res.threadId);
          onClose();
        },
        onError: (err: unknown) => {
          setErrorMsg(errorMessageFor(codeOf(err)));
        },
      });
      return;
    }

    // Broadcast — aba "Um time".
    if (tab === 'team' && selectedTeamId !== null) {
      const payload: BroadcastBody = {
        teamId: selectedTeamId,
        mode,
        body: trimmedBody,
        ...(anchor !== undefined ? { anchorConversationId: anchor } : {}),
      };
      broadcast.mutate(payload, {
        onSuccess: (res) => {
          const first = res.threadIds[0];
          if (first !== undefined) onSent?.(first);
          onClose();
        },
        onError: (err: unknown) => {
          setErrorMsg(errorMessageFor(codeOf(err)));
        },
      });
    }
  };

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={locked ? 'Mensagem privada' : 'Nova mensagem'}
      width={520}
      footer={
        <div className="dm-modal__foot">
          <span className="dm-modal__hint" aria-hidden>
            {!hasRecipient
              ? 'Escolha um destinatário'
              : trimmedBody.length === 0
                ? 'Escreva uma mensagem'
                : 'Enter envia · Shift+Enter quebra linha'}
          </span>
          <button className="btn-ghost-mini" type="button" onClick={onClose} disabled={isPending}>
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {isPending ? 'Enviando…' : 'Enviar'}
          </button>
        </div>
      }
    >
      <div className="dm-modal">
        {/* 1 · Destinatário */}
        <div className="dm-modal__field">
          <span className="dm-modal__label">Destinatário</span>
          {locked ? (
            <div className="dm-modal__locked">
              <span
                className="dm-modal__locked-avatar"
                aria-hidden
                style={{ background: avatarColorFor(lockedRecipient.userId) }}
              >
                {initialsOf(lockedRecipient.name)}
              </span>
              <span className="dm-modal__locked-body">
                <span className="dm-modal__locked-name">{lockedRecipient.name}</span>
                <span className="dm-modal__locked-sub">Atendente</span>
              </span>
              <span className="dm-modal__lock" title="Destinatário fixo">
                <Lock size={12} strokeWidth={1.75} aria-hidden />
                fixo
              </span>
            </div>
          ) : (
            <>
              <div className="dm-modal__toggle" role="group" aria-label="Tipo de destinatário">
                <button
                  type="button"
                  className={`dm-modal__toggle-btn${tab === 'person' ? ' dm-modal__toggle-btn--active' : ''}`}
                  onClick={() => {
                    setTab('person');
                  }}
                >
                  <User size={13} strokeWidth={1.75} aria-hidden />
                  Um atendente
                </button>
                <button
                  type="button"
                  className={`dm-modal__toggle-btn${tab === 'team' ? ' dm-modal__toggle-btn--active' : ''}`}
                  onClick={() => {
                    setTab('team');
                  }}
                >
                  <Users size={13} strokeWidth={1.75} aria-hidden />
                  Um time
                </button>
              </div>

              {tab === 'person' ? (
                <div className="dm-modal__picker" role="listbox" aria-label="Atendentes">
                  {people.length === 0 ? (
                    <span className="dm-modal__empty">
                      {scope.isLoading
                        ? 'Carregando seu escopo…'
                        : emptyPeopleCopy(scope.direction)}
                    </span>
                  ) : (
                    people.map((p) => {
                      const sel = p.userId === selectedUserId;
                      return (
                        <button
                          key={p.userId}
                          type="button"
                          role="option"
                          aria-selected={sel}
                          className={`dm-modal__option${sel ? ' dm-modal__option--selected' : ''}`}
                          onClick={() => {
                            setSelectedUserId(p.userId);
                          }}
                        >
                          <span
                            className="dm-modal__option-avatar"
                            aria-hidden
                            style={{ background: avatarColorFor(p.userId) }}
                          >
                            {initialsOf(p.name)}
                          </span>
                          <span className="dm-modal__option-name">{p.name}</span>
                          {sel && <Check size={15} strokeWidth={2} aria-hidden />}
                        </button>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="dm-modal__picker" role="listbox" aria-label="Times">
                  {scopedTeams.length === 0 ? (
                    <span className="dm-modal__empty">
                      {scope.isLoading
                        ? 'Carregando seu escopo…'
                        : 'Nenhum time que você lidera para envio em massa.'}
                    </span>
                  ) : (
                    scopedTeams.map((t) => {
                      const sel = t.id === selectedTeamId;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          role="option"
                          aria-selected={sel}
                          className={`dm-modal__option${sel ? ' dm-modal__option--selected' : ''}`}
                          onClick={() => {
                            setSelectedTeamId(t.id);
                          }}
                        >
                          <span className="dm-modal__option-team" aria-hidden>
                            <Users size={14} strokeWidth={1.75} />
                          </span>
                          <span className="dm-modal__option-name">{t.name}</span>
                          <span className="dm-modal__option-count">{t.memberCount}</span>
                          {sel && <Check size={15} strokeWidth={2} aria-hidden />}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* 2 · Modo (só quando um time está selecionado) */}
        {showModePicker && (
          <div className="dm-modal__field">
            <span className="dm-modal__label">Modo</span>
            <button
              type="button"
              className={`dm-modal__mode${mode === 'group' ? ' dm-modal__mode--active' : ''}`}
              onClick={() => {
                setMode('group');
              }}
            >
              <span className="dm-modal__mode-dot" aria-hidden>
                {mode === 'group' && <span className="dm-modal__mode-dot-fill" />}
              </span>
              <span className="dm-modal__mode-text">
                <span className="dm-modal__mode-title">Canal do time (todos veem)</span>
                <span className="dm-modal__mode-sub">
                  Uma conversa única. As respostas de todos aparecem para todos.
                </span>
              </span>
            </button>
            <button
              type="button"
              className={`dm-modal__mode${mode === 'individual' ? ' dm-modal__mode--active' : ''}`}
              onClick={() => {
                setMode('individual');
              }}
            >
              <span className="dm-modal__mode-dot" aria-hidden>
                {mode === 'individual' && <span className="dm-modal__mode-dot-fill" />}
              </span>
              <span className="dm-modal__mode-text">
                <span className="dm-modal__mode-title">
                  Aviso individual (cada um recebe em separado)
                </span>
                <span className="dm-modal__mode-sub">
                  Uma conversa 1:1 por pessoa. A resposta volta só para você.
                </span>
              </span>
            </button>
          </div>
        )}

        {/* 3 · Âncora opcional */}
        <div className="dm-modal__field">
          <span className="dm-modal__label">
            Sobre uma conversa <span className="dm-modal__label-opt">· opcional</span>
          </span>
          <button
            type="button"
            className="dm-modal__anchor-trigger"
            onClick={() => {
              setAnchorOpen((v) => !v);
            }}
            aria-expanded={anchorOpen}
          >
            <Link2 size={13} strokeWidth={1.75} aria-hidden />
            <span className="dm-modal__anchor-label">{anchorLabel}</span>
            <ChevronDown size={14} strokeWidth={1.75} aria-hidden />
          </button>
          {anchorOpen && (
            <div className="dm-modal__picker" role="listbox" aria-label="Conversas">
              <button
                type="button"
                role="option"
                aria-selected={anchorId === NO_ANCHOR}
                className={`dm-modal__option${anchorId === NO_ANCHOR ? ' dm-modal__option--selected' : ''}`}
                onClick={() => {
                  setAnchorId(NO_ANCHOR);
                  setAnchorOpen(false);
                }}
              >
                <span className="dm-modal__option-name">Nenhuma</span>
              </button>
              {anchors.map((c) => {
                const sel = c.id === anchorId;
                const label = c.person?.displayName ?? '(sem identidade)';
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    className={`dm-modal__option${sel ? ' dm-modal__option--selected' : ''}`}
                    onClick={() => {
                      setAnchorId(c.id);
                      setAnchorOpen(false);
                    }}
                  >
                    <span className="dm-modal__option-name">{label}</span>
                    {sel && <Check size={15} strokeWidth={2} aria-hidden />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 4 · Mensagem */}
        <div className="dm-modal__field">
          <span className="dm-modal__label">Mensagem</span>
          <textarea
            className="dm-modal__textarea"
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            rows={3}
            placeholder="Escreva a diretiva ou o aviso…"
            aria-label="Mensagem"
          />
        </div>

        {/* Erro honesto inline */}
        {errorMsg !== null && (
          <div className="dm-modal__error banner banner--danger" role="alert">
            <span className="dm-modal__error-icon" aria-hidden>
              <AlertTriangle size={15} strokeWidth={1.9} />
            </span>
            <span>{errorMsg}</span>
          </div>
        )}
      </div>
    </ModalBase>
  );
}
